import React from "react";

import { faArrowLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import * as Sentry from "@sentry/browser";
import cx from "classnames";

import { EmptyHeader, MobileHeader } from "~/auth/components";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isNative, isTeams } from "~/lib/utils";
import type { AuthFlowType } from "~/types/main.d";
import IconButton from "~/ui/legacy/IconButton";
import { ButtonLink } from "~/ui/legacyFellowIndex";

import { AuthBase } from "../AuthBase";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";
import { WorkspacePill, WorkspaceRow, workspaceSort } from "./utils";

import styles from "../css/Auth.module.css";

interface LoginJoinState {
	loading: boolean;
	errorMessage: string | undefined;
	isExpanded: boolean;
}

type JoinWorkspaceResult = { success: true; signup: AuthFlowType } | { success: false; errorMessage?: string };

export default class LoginJoin extends AuthBase<AuthProps, LoginJoinState> {
	state: LoginJoinState = {
		loading: false,
		errorMessage: undefined,
		isExpanded: false,
	};

	documentTitle = "Join | Login";

	getPaneComponent() {
		return WhitelabelPane;
	}

	getPaneProps() {
		return { bgStyle: "purple" as const };
	}

	pickWorkspace = (accountID: string | number, analyticsTag: string) => {
		return () => {
			this.setState({ loading: true });

			let data = new FormData();

			data.append("account_id", String(accountID));
			data.append("for_mobile", String(isNative));

			/* eslint-disable camelcase */
			const analyticsData = {
				account_id: accountID,
				feature: "Authentication",
				workspace_choice_type: analyticsTag,
			};
			/* eslint-enable camelcase */

			fetch("/auth/save/join/", {
				method: "POST",
				body: data,
				headers: {
					"x-client-session-id": window.CLIENT_SESSION_ID,
					"x-csrftoken": window.CSRF_TOKEN,
					"x-requested-with": "XMLHttpRequest",
				},
			})
				.then((res: Response) => {
					return res.json();
				})
				.then((data: JoinWorkspaceResult) => {
					dispatchAnalyticsTrack("Login Join Step Completed", analyticsData);
					window.setTimeout(() => {
						if (data.success) {
							if (data.signup.redirect) {
								window.location.href = data.signup.redirect;
							} else if (data.signup.next) {
								this.context.setAuthContext(data.signup);
								this.props.history.push(data.signup.next, this.props.location.state);
							}
						} else if (data.errorMessage) {
							dispatchAnalyticsTrack("Login Join Step Failed", analyticsData);
							this.setState({ errorMessage: data.errorMessage });
						}
					}, 100);
				})
				.catch(err => {
					Sentry.captureException(err);
					dispatchAnalyticsTrack("Login Join Step Failed", analyticsData);
					this.setState({ loading: false, errorMessage: `An error occurred: ${err}` });
				});
		};
	};

	expandWorkspaceList() {
		return () => {
			this.setState({ isExpanded: true });
		};
	}

	renderStepTeams() {
		const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
		const { loading } = this.state;

		const domainAccounts =
			this.context.domain_accounts?.sort(workspaceSort).map(account => (
				<a
					key={account.id}
					className={styles.workspaceButton}
					rel="noopener"
					target={"_blank"}
					href={`https://${account.subdomain}${rootDomain}/auth/signup`}
				>
					<WorkspacePill account={account} />
				</a>
			)) ?? [];

		const memberAccounts =
			this.context.member_accounts?.sort(workspaceSort).map(account => (
				<button
					key={account.id}
					className={styles.workspaceButton}
					disabled={loading}
					onClick={this.pickWorkspace(account.id, "member")}
				>
					<WorkspacePill member account={account} />
				</button>
			)) ?? [];

		return (
			<div className={styles.authFlex}>
				{this.props.history.length > 1 && (
					<IconButton title="Back" icon={faArrowLeft} className={styles.backButton} onClick={this.onBackClick} />
				)}

				<h3>That email has multiple workspaces associated</h3>

				{memberAccounts.length > 0 && (
					<>
						<h5>Log in to one of your workspaces</h5>
						<div className={styles.workspaceButtons}>{memberAccounts}</div>
					</>
				)}

				{memberAccounts.length > 0 && domainAccounts.length > 0 && <h4>or</h4>}

				{domainAccounts.length > 0 && (
					<>
						<h5>Join an existing workspace</h5>
						<div className={styles.workspaceButtons}>{domainAccounts}</div>
					</>
				)}
			</div>
		);
	}

	renderStep() {
		const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
		const { loading } = this.state;

		if (isTeams) return this.renderStepTeams();

		const domainAccounts =
			this.context.domain_accounts
				?.sort(workspaceSort)
				.map(account => (
					<WorkspaceRow
						key={account.id}
						account={account}
						subdomain={account.subdomain ?? ""}
						accountType="domain"
						loginUrl={`https://${account.subdomain}${rootDomain}/auth/signup`}
					/>
				)) ?? [];

		const memberAccounts =
			this.context.member_accounts
				?.sort(workspaceSort)
				.map(account => (
					<WorkspaceRow
						key={account.id}
						account={account}
						subdomain={account.subdomain ?? ""}
						loading={loading}
						accountType="member"
						pickWorkspace={this.pickWorkspace(account.id, "member")}
					/>
				)) ?? [];

		const guestAccounts =
			this.context.guest_accounts
				?.sort(workspaceSort)
				.map(account => (
					<WorkspaceRow
						key={account.id}
						account={account}
						subdomain={account.subdomain ?? ""}
						loading={loading}
						accountType="guest"
						pickWorkspace={this.pickWorkspace(account.id, "guest")}
					/>
				)) ?? [];

		const allAccounts = [...memberAccounts, ...guestAccounts, ...domainAccounts];

		const initialNumVisibleAccounts = 5;
		const visibleAccounts = this.state.isExpanded ? allAccounts : allAccounts.slice(0, initialNumVisibleAccounts);
		const extraAccounts = allAccounts.length - initialNumVisibleAccounts;

		return (
			<div className={styles.authFlex}>
				{(memberAccounts.length > 0 || guestAccounts.length > 0 || domainAccounts.length > 0) && (
					<div className={styles.loggedInWorkspacesContainer}>
						<div className={styles.loggedInWorkspacesSubheader}>
							Choose a workspace you&apos;re already logged into:
						</div>
						<div className={cx(styles.workspaceList, { [styles.workspaceListDisabled]: loading })}>
							{visibleAccounts}
							{!this.state.isExpanded && allAccounts.length > initialNumVisibleAccounts && (
								<div className={styles.workspaceListMore}>
									<div className={styles.workspaceListMoreText}>
										{extraAccounts} more workspace
										{extraAccounts > 1 ? "s" : ""}
									</div>
									<ButtonLink className={styles.showAllWorkspaces} onClick={this.expandWorkspaceList()}>
										Show all workspaces
									</ButtonLink>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		);
	}

	getMobileHeaderComponent() {
		if (isTeams) {
			return EmptyHeader;
		} else {
			return MobileHeader;
		}
	}
}
